import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "../lib/api";

interface LookupItem {
  id: number;
  name: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

interface ValuerItem {
  id: number;
  name: string;
  shortCode: string;
  qualifications: string | null;
  email: string | null;
  isActive: boolean;
  signatureUrl: string | null;
}

interface InflationDefaults {
  inflationYear1: number;
  inflationYear2: number;
  inflationYear3: number;
  inflationYear4: number;
  demoPlanMonths: number;
  constructionMonths: number;
  firstYearMonths: number;
}

interface SettingsUser {
  id: string;
  email: string;
  name: string;
  role: "ADMIN" | "VALUER" | "VIEWER";
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  if (response.status === 401) {
    window.location.href = `/api/auth/login?returnTo=${encodeURIComponent(window.location.pathname)}`;
    throw new ApiError(401, "AUTH_REQUIRED", "Not authenticated");
  }
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText, code: "UNKNOWN" }));
    throw new ApiError(response.status, body.code ?? "UNKNOWN", body.error ?? "Request failed", body.details);
  }
  return response.json() as Promise<T>;
}

// Property types
export function useCreatePropertyType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; sortOrder?: number }) =>
      api.post<{ item: LookupItem }>("/api/property-types", body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["lookups", "property-types"] }),
  });
}

export function useUpdatePropertyType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number; name?: string; isActive?: boolean; sortOrder?: number }) =>
      api.put<{ item: LookupItem }>(`/api/property-types/${id}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["lookups", "property-types"] }),
  });
}

export function useDeletePropertyType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<{ item: LookupItem }>(`/api/property-types/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["lookups", "property-types"] }),
  });
}

// Subsoil options
export function useCreateSubsoilOption() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; sortOrder?: number }) =>
      api.post<{ item: LookupItem }>("/api/subsoil-options", body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["lookups", "subsoil-options"] }),
  });
}

export function useUpdateSubsoilOption() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number; name?: string; isActive?: boolean; sortOrder?: number }) =>
      api.put<{ item: LookupItem }>(`/api/subsoil-options/${id}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["lookups", "subsoil-options"] }),
  });
}

export function useDeleteSubsoilOption() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<{ item: LookupItem }>(`/api/subsoil-options/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["lookups", "subsoil-options"] }),
  });
}

// Valuers
interface ValuerWriteInput {
  name: string;
  shortCode: string;
  qualifications?: string;
  email?: string;
  isActive?: boolean;
}

export function useCreateValuer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ValuerWriteInput) => api.post<{ valuer: ValuerItem }>("/api/valuers", body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["lookups", "valuers"] }),
  });
}

export function useUpdateValuer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number } & Partial<ValuerWriteInput>) =>
      api.put<{ valuer: ValuerItem }>(`/api/valuers/${id}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["lookups", "valuers"] }),
  });
}

export function useDeleteValuer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<{ valuer: ValuerItem }>(`/api/valuers/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["lookups", "valuers"] }),
  });
}

export function useUploadValuerSignature() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ valuerId, file }: { valuerId: number; file: File }) => {
      const formData = new FormData();
      formData.append("file", file);
      const response = await fetch(`/api/valuers/${valuerId}/signature`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      return parseJsonResponse<{ valuer: { id: number; signatureUrl: string } }>(response);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["lookups", "valuers"] }),
  });
}

// Inflation defaults
export function useInflationDefaults() {
  return useQuery({
    queryKey: ["settings", "inflation-defaults"],
    queryFn: () => api.get<InflationDefaults>("/api/settings/inflation-defaults"),
  });
}

export function useUpdateInflationDefaults() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: InflationDefaults) => api.put<InflationDefaults>("/api/settings/inflation-defaults", body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["settings", "inflation-defaults"] }),
  });
}

export function useReferencePrefix() {
  return useQuery({
    queryKey: ["settings", "reference-prefix"],
    queryFn: () => api.get<{ referencePrefix: string }>("/api/settings/reference-prefix"),
  });
}

export function useUpdateReferencePrefix() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { referencePrefix: string }) =>
      api.put<{ referencePrefix: string }>("/api/settings/reference-prefix", body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["settings", "reference-prefix"] }),
  });
}

// Users
export function useSettingsUsers() {
  return useQuery({
    queryKey: ["settings", "users"],
    queryFn: () => api.get<{ users: SettingsUser[] }>("/api/settings/users"),
  });
}

export function useUpdateUserRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, role }: { id: string; role: SettingsUser["role"] }) => {
      const response = await fetch(`/api/settings/users/${id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role }),
      });
      return parseJsonResponse<{ user: SettingsUser }>(response);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["settings", "users"] }),
  });
}
